// types
import { handleApiRequest } from 'Utils/handleApiRequest';
import { separateMultiDayTimeCardsForTimeSheet } from 'Containers/TimeTracking/helpers';

import { Duration, isSameDay } from 'date-fns';
import { downloadLocal } from 'Utils/helpers';
import { TimeTrackingCard, TimeCardFilters, TimeTrackingCardTimesheet } from './types';

// Convert total_hours decimal from API to a Duration object
const totalHoursToDuration = (totalHours: number | null | undefined): Duration | undefined => {
  if (totalHours == null) return undefined;
  const hours = Math.floor(totalHours);
  const minutes = Math.round((totalHours - hours) * 60);
  return { hours, minutes };
};

export const SET_USER_TIME_CARDS = 'SET_USER_TIME_CARDS';
export const SET_USER_CLOCKED_IN_TIME_CARDS = 'SET_USER_CLOCKED_IN_TIME_CARDS';
export const SET_PROJECT_TIME_CARDS = 'SET_PROJECT_TIME_CARDS';
export const CLOCK_IN_SUCCESS = 'CLOCK_IN_SUCCESS';
export const CLOCK_OUT_SUCCESS = 'CLOCK_OUT_SUCCESS';
export const REFRESH_TIME_CARDS = 'REFRESH_TIME_CARDS';
export const REFRESH_COMPANY_TIME_CARDS = 'REFRESH_COMPANY_TIME_CARDS';
export const TIME_CARD_CREATED = 'TIME_CARD_CREATED';
export const TIME_CARD_UPDATED = 'TIME_CARD_UPDATED';
export const TIME_CARD_NOTE_CREATED = 'TIME_CARD_NOTE_CREATED';
export const SET_COMPANY_TIME_CARDS = 'SET_COMPANY_TIME_CARDS';
export const SET_EMPLOYEES_FILTERS = 'SET_EMPLOYEES_FILTERS';
export const SET_PROJECT_NUMBERS_FILTERS = 'SET_PROJECT_NUMBERS_FILTERS';
export const SET_ADDRESSES_FILTERS = 'SET_ADDRESSES_FILTERS';
export const SET_PROJECT_NUMBERS_SIMPLE_LIST = 'SET_PROJECT_NUMBERS_SIMPLE_LIST';
export const SET_ADDRESSES_SIMPLE_LIST = 'SET_ADDRESSES_SIMPLE_LIST';
export const SET_EMPLOYEES_DROPDOWN = 'SET_EMPLOYEES_DROPDOWN';
export const SET_PROJECTS_DROPDOWN = 'SET_PROJECTS_DROPDOWN';
export const CREATE_TIME_CARD_ERRORS = 'CREATE_TIME_CARD_ERRORS';
export const UPDATE_TIME_CARD_ERRORS = 'UPDATE_TIME_CARD_ERRORS';
export const CREATE_TIME_CARD_NOTE_ERRORS = 'CREATE_TIME_CARD_NOTE_ERRORS';
export const SET_TIME_CARD_NOTES = 'SET_TIME_CARD_NOTES';
export const SET_TIMECARD_TYPES = 'SET_TIMECARD_TYPES';
export const CREATE_TIMECARD_TYPE_ERRORS = 'CREATE_TIMECARD_TYPE_ERRORS';
export const UPDATE_TIMECARD_TYPE_ERRORS = 'UPDATE_TIMECARD_TYPE_ERRORS';
export const REFRESH_TIMECARD_TYPES = 'REFRESH_TIMECARD_TYPES';
export const SET_COMPANY_TIME_CARD_RATES = 'SET_COMPANY_TIME_CARD_RATES';
export const SET_COMPANY_DEFAULT_LABOR_RATE = 'SET_COMPANY_DEFAULT_LABOR_RATE';
export const SET_PROJECT_TIME_CARD_RATES = 'SET_PROJECT_TIME_CARD_RATES';

interface ActionTypes {
  SET_USER_TIME_CARDS: TimeTrackingCard[];
  SET_USER_CLOCKED_IN_TIME_CARDS: TimeTrackingCard[];
  CLOCK_IN_SUCCESS: boolean;
  CLOCK_OUT_SUCCESS: boolean;
  REFRESH_TIME_CARDS: boolean;
  REFRESH_COMPANY_TIME_CARDS: boolean;
  TIME_CARD_CREATED: boolean;
  TIME_CARD_UPDATED: boolean;
  TIME_CARD_NOTE_CREATED: boolean;
  SET_COMPANY_TIME_CARDS: any[];
  SET_EMPLOYEES_FILTERS: any[];
  SET_PROJECT_NUMBERS_FILTERS: any[];
  SET_ADDRESSES_FILTERS: any[];
  SET_EMPLOYEES_DROPDOWN: any[];
  SET_PROJECT_NUMBERS_SIMPLE_LIST: any[];
  SET_ADDRESSES_SIMPLE_LIST: any[];
  SET_PROJECTS_DROPDOWN: any[];
  CREATE_TIME_CARD_ERRORS: any;
  UPDATE_TIME_CARD_ERRORS: any;
  CREATE_TIME_CARD_NOTE_ERRORS: any;
  SET_TIME_CARD_NOTES: any[];
  SET_TIMECARD_TYPES: any[];
  CREATE_TIMECARD_TYPE_ERRORS: any;
  UPDATE_TIMECARD_TYPE_ERRORS: any;
  REFRESH_TIMECARD_TYPES: boolean;
  SET_COMPANY_TIME_CARD_RATES: any[];
  SET_COMPANY_DEFAULT_LABOR_RATE: number | null;
  SET_PROJECT_TIME_CARD_RATES: any[];
}

interface MessageAction {
  type: keyof ActionTypes;
  payload: any;
}

export type TimeTrackingTypes = MessageAction;

/*
 * NON-API THUNKS
 * */
export const setClockInSuccess = (status: boolean) => async (dispatch: any) =>
  dispatch({
    type: CLOCK_IN_SUCCESS,
    payload: status,
  });

export const setClockOutSuccess = (status: boolean) => async (dispatch: any) =>
  dispatch({
    type: CLOCK_OUT_SUCCESS,
    payload: status,
  });

export const setRefreshTimeCards = (status: boolean) => async (dispatch: any) =>
  dispatch({
    type: REFRESH_TIME_CARDS,
    payload: status,
  });

export const setRefreshCompanyTimeCards = (status: boolean) => async (dispatch: any) =>
  dispatch({
    type: REFRESH_COMPANY_TIME_CARDS,
    payload: status,
  });

export const setTimeCardNoteCreated = (value: boolean) => (dispatch) => {
  dispatch({
    type: TIME_CARD_NOTE_CREATED,
    payload: value,
  });
};

export const setTimeCardNotes = (value: any[]) => (dispatch) => {
  dispatch({
    type: SET_TIME_CARD_NOTES,
    payload: value,
  });
};

/*
 * API THUNKS
 * */
/* eslint-disable */
export const setUserTimeCards =
  (userId: number) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.get(`users/${userId}/timecards`, {
        params: {
          include: 'timecardType,project,project.address',
        },
      })
    );

    if (response?.data) {
      const userTimeCards: TimeTrackingCard[] = response.data.map((card) => {
        const timeInParsed = Date.parse(card.time_in);
        const timeOutParsed = card.time_out ? Date.parse(card.time_out) : null;

        const { project: cardProject } = card;
        const newCard: TimeTrackingCard = {
          id: card.id.toString(),
          projectId: cardProject?.id.toString(),
          address: cardProject?.address?.address,
          timeIn: card.time_in,
          timeOut: card.time_out,
          timecardType: card.timecard_type,
          elapsed: card.elapsed,
          totalHours: card.total_hours,
          sameDay: isSameDay(timeInParsed, timeOutParsed ?? new Date()),
          duration: totalHoursToDuration(card.total_hours),
        };

        return newCard;
      });

      dispatch({
        type: SET_USER_TIME_CARDS,
        payload: userTimeCards,
      });
    }
  };

export const setUserClockedInTimeCards =
  (userId: number) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.get(`users/${userId}/timecards`, {
        params: {
          'filter[no_time_outs]': 1,
          include: 'timecardType,project,project.address',
        },
      })
    );

    if (response?.data) {
      const userClockedCards: TimeTrackingCard[] = response.data.map((card) => {
        const timeInParsed = Date.parse(card.time_in);
        const timeOutParsed = card.time_out ? Date.parse(card.time_out) : null;
        const { project: cardProject } = card;

        const newCard: TimeTrackingCard = {
          id: card.id.toString(),
          projectId: cardProject?.id.toString(),
          address: cardProject?.address?.address,
          timeIn: card.time_in,
          timeOut: card.time_out,
          timecardType: card.timecard_type,
          sameDay: isSameDay(timeInParsed, timeOutParsed ?? new Date()),
        };

        return newCard;
      });

      dispatch({
        type: SET_USER_CLOCKED_IN_TIME_CARDS,
        payload: userClockedCards,
      });
    }
  };

export const setUserTimeCardsForTimeRange =
  (userId: number, startDate: string, endDate: string) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.get(`users/${userId}/timecards`, {
        params: {
          include: 'timecardType,project,project.address',
          'filter[starts_between]': `${startDate},${endDate}`,
        },
      })
    );

    if (response?.data) {
      const userTimeCards: TimeTrackingCard[] = response.data.map((card) => {
        const timeInParsed = Date.parse(card.time_in);
        const timeOutParsed = card.time_out ? Date.parse(card.time_out) : null;
        const { project: cardProject } = card;
        const newCard: TimeTrackingCard = {
          id: card.id.toString(),
          projectId: cardProject?.id.toString(),
          address: cardProject?.address?.address,
          projectNumber: cardProject?.uid,
          timeIn: card.time_in,
          timeOut: card.time_out,
          timecardType: card.timecard_type,
          elapsed: card.elapsed,
          totalHours: card.total_hours,
          sameDay: isSameDay(timeInParsed, timeOutParsed ?? new Date()),
          duration: totalHoursToDuration(card.total_hours),
        };

        return newCard;
      });

      return userTimeCards;
    } else {
      return [];
    }
  };

export const createTimeCard =
  (
    projectId: number,
    timeIn: string,
    timecardType: number,
    timeOut?: string,
    userId?: string,
    onTimeCardCreated?: any
  ) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.post(`projects/${projectId}/timecards`, {
        time_in: timeIn,
        timecard_type_id: timecardType,
        time_out: timeOut,
        user_id: userId,
      }),
      CREATE_TIME_CARD_ERRORS
    );

    if (response?.data) {
      dispatch(setRefreshCompanyTimeCards(true));
      if (onTimeCardCreated) {
        onTimeCardCreated();
      }
    }
  };

export const updateTimeCard =
  (timeCardId: number, timeCardType: number, userId: number, projectId: number, timeIn: string, timeOut: string, onTimeCardEdited?: any) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.put(`timecards/${timeCardId}`, {
        time_in: timeIn,
        timecard_type_id: timeCardType,
        time_out: timeOut,
        user_id: userId,
        project_id: projectId,
      }),
      UPDATE_TIME_CARD_ERRORS
    );

    if (response?.data) {
      dispatch(setRefreshCompanyTimeCards(true));
      if (onTimeCardEdited) {
        onTimeCardEdited();
      }
    }
  };

export const deleteTimeCard =
  (timeCardId: number, onTimeCardDeleted?: any) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(dispatch, utils.Api.delete(`timecards/${timeCardId}`));

    if (typeof response === 'string') {
      dispatch(setRefreshCompanyTimeCards(true));
      if (onTimeCardDeleted) {
        onTimeCardDeleted();
      }
    }
  };

export const clockIn =
  (projectId: number, timeIn: string, timeCardType: number, refreshTimeCards: boolean = false) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.post(`projects/${projectId}/timecards`, {
        time_in: timeIn,
        timecard_type_id: timeCardType,
      })
    );

    if (response?.data) {
      dispatch(setClockInSuccess(true));
      // post-processing for when user clocks in while clocked in to another project (i.e. they want to switch)
      // see the clockOut comment for details
      if (refreshTimeCards) {
        dispatch(setRefreshTimeCards(true));
      }
    }
  };

export const clockOut =
  (timeCard: TimeTrackingCard, timeOut: string, willClockInNext: boolean = false) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const { id: timeCardId, timeIn, timecardType: timecardType } = timeCard;
    const response = await handleApiRequest(
      dispatch,
      utils.Api.put(`timecards/${timeCardId}`, {
        time_in: timeIn,
        time_out: timeOut,
        timecard_type_id: timecardType.id,
      })
    );

    if (response?.data) {
      // When user clocks in while clocked in to another project (i.e. they want to switch), don't set
      // clockOutSuccess to true. Otherwise the post-processing code can run multiple times.
      // That can be handled in the clock in process.
      if (!willClockInNext) {
        dispatch(setClockOutSuccess(true));
      }
    }
  };

export const clockOutUserTimeCardForProject =
  (userId: number, projectId: number) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.get(`users/${userId}/timecards`, {
        params: {
          include: 'timecardType',
          'filter[no_time_outs]': 1,
          'filter[project_id]': projectId,
        },
      })
    );

    if (response?.data?.length > 0) {
      const clockOutTime = new Date().toISOString();
      const userClockedInTimeCards: TimeTrackingCard[] = response.data.map((card) => {
        const newCard: TimeTrackingCard = {
          id: card.id.toString(),
          timeIn: card.time_in,
          timecardType: card.timecard_type,
          sameDay: true,
        };

        return newCard;
      });

      userClockedInTimeCards.forEach((timeCard: TimeTrackingCard) => {
        dispatch(clockOut(timeCard, clockOutTime, true));
      });

      dispatch(setClockOutSuccess(true));
    }
  };

export const setProjectTimeCards = (projectId: number, page = 1) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.get(`projects/${projectId}/timecards`, {
        params: {
          page,
        },
      })
    );

    if (response?.data) {
      dispatch({
        type: SET_USER_TIME_CARDS,
        payload: {
          ...response,
          page,
        },
      });
    }
  };

export const listCompanyTimeCards =
  (companyId: number, filters: TimeCardFilters, page = 1, limit = 50, sort = '-time_in') =>
  async (dispatch: any, _getState = null, utils: any) => {
    const params = {
      include: 'timecardType,project,project.address,user,notesCount',
      page,
      per_page: limit,
      sort,
      'filter[project_id]': filters.projectId,
      'filter[user_id]': filters.employeeId,
      'filter[address_id]': filters.addressId,
    };

    if (filters.startsBetween) {
      params['filter[starts_between]'] = `${filters.startsBetween.startDate},${filters.startsBetween.endDate}`;
    }

    const response = await handleApiRequest(
      dispatch,
      utils.Api.get(`companies/${companyId}/timecards`, {
        params,
      })
    );

    // if (filters) {
    //   console.log('filters', filters);
    // }

    if (response?.data) {
      const companyTimeCards: TimeTrackingCard[] = response.data.map((card) => {
        const timeInParsed = Date.parse(card.time_in);
        const timeOutParsed = card.time_out ? Date.parse(card.time_out) : null;
        const { project: cardProject } = card;
        const newCard: TimeTrackingCard = {
          id: card.id.toString(),
          projectId: cardProject?.id.toString(),
          projectNumber: cardProject?.uid,
          timeIn: card.time_in,
          timeOut: card.time_out,
          timecardType: card.timecard_type,
          elapsed: card.elapsed,
          totalHours: card.total_hours,
          sameDay: isSameDay(timeInParsed, timeOutParsed ?? new Date()),
          // card.user is null when the timecard's user was deactivated (soft-deleted)
          // and the API doesn't return trashed users — keep the row visible instead
          // of crashing the whole list.
          user: card.user?.full_name ?? 'Deactivated user',
          userId: card.user?.id,
          notesCount: card.notes_count,
          duration: totalHoursToDuration(card.total_hours),
          cost: card.cost,
          rate: card.rate,
        };

        if (cardProject) {
          const { address } = cardProject;
          newCard.address = address.address;
          newCard.address2 = `${address.city}, ${address.state}, ${address.zip}`;
        }

        return newCard;
      });
      // console.log({
      //   data: companyTimeCards,
      //   meta: response.meta ?? {},
      //   links: response.links ?? {},
      // });

      const companyTimeCardsSplitByDay: TimeTrackingCardTimesheet[] = companyTimeCards.reduce(
        (cards: TimeTrackingCardTimesheet[], currentCard: TimeTrackingCard) => {
          if (currentCard.sameDay) {
            cards.push(currentCard);
          } else {
            const newCards: TimeTrackingCardTimesheet[] = separateMultiDayTimeCardsForTimeSheet(currentCard);
            cards.push(...newCards);
          }

          return cards;
        },
        []
      );
      dispatch({
        type: SET_COMPANY_TIME_CARDS,
        payload: {
          data: companyTimeCardsSplitByDay,
          meta: response.meta ?? {},
          links: response.links ?? {},
        },
      });
    } else {
      dispatch({
        type: SET_COMPANY_TIME_CARDS,
        payload: response,
      });
    }
  };

export const listEmployeesForTimesheets =
  (companyId: number, searchValue = '') =>
  async (dispatch: any, _getState = null, utils: any) => {
    let lastPage = false;
    let currentTimecards = [];
    let page = 1;

    // Fetch timecards to extract only employees that have timecards
    while (!lastPage) {
      const response = await handleApiRequest(
        dispatch,
        utils.Api.get(`companies/${companyId}/timecards`, {
          params: {
            include: 'user',
            page,
            limit: 100,
          },
        })
      );

      lastPage = response?.meta?.current_page === response?.meta?.last_page;

      page += 1;
      if (response?.data) {
        currentTimecards.push(...response.data);
      }
    }

    // Extract unique employees from timecards
    const seenUserIds = new Set<number>();
    const employeeNameIds: { id: number; name: string }[] = [];

    currentTimecards.forEach((card: any) => {
      const { user } = card;
      if (!user || seenUserIds.has(user.id)) return;
      seenUserIds.add(user.id);

      // Apply search filter client-side
      if (searchValue) {
        const search = searchValue.toLowerCase();
        if (!user.full_name?.toLowerCase().includes(search)) return;
      }

      employeeNameIds.push({
        id: user.id,
        name: user.full_name,
      });
    });

    dispatch({
      type: SET_EMPLOYEES_FILTERS,
      payload: employeeNameIds,
    });
  };

export const listProjectsAndAddressesForTimesheets =
  (companyId: number, searchValue = '') =>
  async (dispatch: any, _getState = null, utils: any) => {
    let lastPage = false;
    let currentTimecards = [];
    let page = 1;

    // Fetch timecards to extract only projects that have timecards
    while (!lastPage) {
      const response = await handleApiRequest(
        dispatch,
        utils.Api.get(`companies/${companyId}/timecards`, {
          params: {
            include: 'project,project.address',
            page,
            limit: 100,
          },
        })
      );

      lastPage = response?.meta?.current_page === response?.meta?.last_page;

      page += 1;
      if (response?.data) {
        currentTimecards.push(...response.data);
      }
    }

    // Extract unique projects from timecards
    const seenProjectIds = new Set<number>();
    const seenAddressIds = new Set<number>();
    const projectNumbers: { id: number; name: string }[] = [];
    const addresses: { id: number; name: string }[] = [];
    const projectInfoSimple: { id: number; name: string; name2: string }[] = [];

    currentTimecards.forEach((card) => {
      const { project } = card;
      if (!project || seenProjectIds.has(project.id)) return;

      seenProjectIds.add(project.id);
      const projectNumber = project.uid;

      // Apply search filter client-side
      if (searchValue) {
        const search = searchValue.toLowerCase();
        const matchesProject = projectNumber?.toLowerCase().includes(search);
        const matchesAddress = project.address?.address?.toLowerCase().includes(search);
        if (!matchesProject && !matchesAddress) return;
      }

      projectNumbers.push({
        id: project.id,
        name: projectNumber,
      });

      if (project.address && !seenAddressIds.has(project.address.id)) {
        seenAddressIds.add(project.address.id);
        addresses.push({
          id: project.address.id,
          name: project.address.address,
        });

        projectInfoSimple.push({
          id: project.id,
          name: project.address.address,
          name2: projectNumber,
        });
      }
    });

    dispatch({
      type: SET_PROJECT_NUMBERS_FILTERS,
      payload: projectNumbers,
    });
    dispatch({
      type: SET_ADDRESSES_FILTERS,
      payload: addresses,
    });
  };

export const listAllEmployeesForModals =
  (companyId: number) =>
  async (dispatch: any, _getState = null, utils: any) => {
    let lastPage = false;
    let currentEmployees = [];
    let page = 1;

    while (!lastPage) {
      const response = await handleApiRequest(
        dispatch,
        utils.Api.get(`companies/${companyId}/users`, {
          params: {
            page,
            limit: 100,
            'filter[include_crew]': 1,
          },
        })
      );

      lastPage = response?.meta?.current_page === response?.meta?.last_page;
      page += 1;
      if (response?.data) {
        currentEmployees.push(...response.data);
      }
    }

    const employeeNameIds = currentEmployees.map((employee: any) => ({
      id: employee.id,
      name: employee.full_name,
    }));

    dispatch({
      type: SET_EMPLOYEES_DROPDOWN,
      payload: employeeNameIds,
    });
  };

export const listAllProjectsForModals =
  (companyId: number) =>
  async (dispatch: any, _getState = null, utils: any) => {
    let lastPage = false;
    let currentProjects = [];
    let page = 1;

    while (!lastPage) {
      const response = await handleApiRequest(
        dispatch,
        utils.Api.get(`companies/${companyId}/projects`, {
          params: {
            include: 'address',
            sort: '-created_at',
            page,
          },
        })
      );

      lastPage = response?.meta?.current_page === response?.meta?.last_page;
      page += 1;
      if (response?.data) {
        currentProjects.push(...response.data);
      }
    }

    const projectInfoSimple = currentProjects.map((project: any) => ({
      id: project.id,
      name: project.address?.address || '',
      name2: project.uid,
    }));

    dispatch({
      type: SET_PROJECTS_DROPDOWN,
      payload: projectInfoSimple,
    });
  };

export const createTimecardNote =
  (timecardId: number, requestData: any, onTimeCardNoteCreated?: any) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.post(`timecards/${timecardId}/notes`, requestData),
      CREATE_TIME_CARD_NOTE_ERRORS
    );

    if (response?.data) {
      // dispatch(setTimeCardNoteCreated(true));
      dispatch(listTimeCardNotes(timecardId));
      if (onTimeCardNoteCreated) {
        onTimeCardNoteCreated();
      }
    }
  };

export const listTimeCardNotes =
  (timecardId: number, startDate?: any, endDate?: string) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const params = {};
    if (startDate && endDate) {
      params['filter[starts_between]'] = `${startDate},${endDate}`;
    }

    const response = await handleApiRequest(dispatch, utils.Api.get(`timecards/${timecardId}/notes`, { params }));

    if (response?.data) {
      const { data } = response;
      dispatch({
        type: SET_TIME_CARD_NOTES,
        payload: data,
      });
    }
  };

export const exportCompanyTimeCardCsv =
  (companyId: number, filters: TimeCardFilters) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const params = {
      'filter[project_id]': filters.projectId,
      'filter[user_id]': filters.employeeId,
      'filter[address_id]': filters.addressId,
    };

    if (filters.startsBetween) {
      params['filter[starts_between]'] = `${filters.startsBetween.startDate},${filters.startsBetween.endDate}`;
    }

    const response = await handleApiRequest(
      dispatch,
      utils.Api.csvGet(`companies/${companyId}/timecards/export`, {
        params,
      })
    );

    if (response) {
      const name = 'timesheets.csv';
      downloadLocal(response, name);
    }
  };

/*
 * TIMECARD TYPES API THUNKS
 * */
export const listTimecardTypes = () => async (dispatch: any, _getState = null, utils: any) => {
  const response = await handleApiRequest(dispatch, utils.Api.get('timecard-types'));

  if (response?.data) {
    dispatch({
      type: SET_TIMECARD_TYPES,
      payload: response.data,
    });
  }
};

export const createTimecardType =
  (name: string, onSuccess?: () => void) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.post('timecard-types', { name }),
      CREATE_TIMECARD_TYPE_ERRORS
    );

    if (response?.data) {
      dispatch(listTimecardTypes());
      if (onSuccess) {
        onSuccess();
      }
    }
  };

export const updateTimecardType =
  (id: number, name: string, onSuccess?: () => void) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.put(`timecard-types/${id}`, { name }),
      UPDATE_TIMECARD_TYPE_ERRORS
    );

    if (response?.data) {
      dispatch(listTimecardTypes());
      if (onSuccess) {
        onSuccess();
      }
    }
  };

export const deleteTimecardType =
  (id: number, onSuccess?: () => void) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(dispatch, utils.Api.delete(`timecard-types/${id}`));

    if (typeof response === 'string') {
      dispatch(listTimecardTypes());
      if (onSuccess) {
        onSuccess();
      }
    }
  };

export const listCompanyTimecardRates =
  (companyId: number) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(dispatch, utils.Api.get(`companies/${companyId}/timecard-rates`));

    if (response?.data) {
      dispatch({
        type: SET_COMPANY_TIME_CARD_RATES,
        payload: response.data,
      });
    }
  };

export const setCompanyTimecardTypeRate =
  (companyId: number, timecardTypeId: number, rate: number, onSuccess?: () => void) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.post(`companies/${companyId}/timecard-rates`, {
        timecard_type_id: timecardTypeId,
        rate,
      })
    );

    if (response?.data) {
      dispatch(listCompanyTimecardRates(companyId));
      if (onSuccess) {
        onSuccess();
      }
    }
  };

export const updateCompanyTimecardTypeRate =
  (companyId: number, timecardTypeId: number, rate: number, onSuccess?: () => void) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.patch(`companies/${companyId}/timecard-rates/${timecardTypeId}`, { rate })
    );

    if (response?.data) {
      dispatch(listCompanyTimecardRates(companyId));
      if (onSuccess) {
        onSuccess();
      }
    }
  };

export const deleteCompanyTimecardTypeRate =
  (companyId: number, timecardTypeId: number, onSuccess?: () => void) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.delete(`companies/${companyId}/timecard-rates/${timecardTypeId}`)
    );

    if (typeof response === 'string') {
      dispatch(listCompanyTimecardRates(companyId));
      if (onSuccess) {
        onSuccess();
      }
    }
  };

export const setCompanyDefaultLaborRate =
  (companyId: number, rate: number, onSuccess?: () => void) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.put(`companies/${companyId}`, { default_labor_rate: rate })
    );

    if (response?.data) {
      dispatch({
        type: SET_COMPANY_DEFAULT_LABOR_RATE,
        payload: rate,
      });
      if (onSuccess) {
        onSuccess();
      }
    }
  };

export const listProjectTimecardRates =
  (projectId: number) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(dispatch, utils.Api.get(`projects/${projectId}/timecard-rates`));

    if (response?.data) {
      dispatch({
        type: SET_PROJECT_TIME_CARD_RATES,
        payload: response.data,
      });
    }
  };

export const setProjectTimecardTypeRate =
  (projectId: number, timecardTypeId: number, rate: number, onSuccess?: () => void) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.post(`projects/${projectId}/timecard-rates`, {
        timecard_type_id: timecardTypeId,
        rate,
      })
    );

    if (response?.data) {
      dispatch(listProjectTimecardRates(projectId));
      if (onSuccess) {
        onSuccess();
      }
    }
  };

export const updateProjectTimecardTypeRate =
  (projectId: number, timecardTypeId: number, rate: number, onSuccess?: () => void) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.patch(`projects/${projectId}/timecard-rates/${timecardTypeId}`, { rate })
    );

    if (response?.data) {
      dispatch(listProjectTimecardRates(projectId));
      if (onSuccess) {
        onSuccess();
      }
    }
  };

export const deleteProjectTimecardTypeRate =
  (projectId: number, timecardTypeId: number, onSuccess?: () => void) =>
  async (dispatch: any, _getState = null, utils: any) => {
    const response = await handleApiRequest(
      dispatch,
      utils.Api.delete(`projects/${projectId}/timecard-rates/${timecardTypeId}`)
    );

    if (typeof response === 'string') {
      dispatch(listProjectTimecardRates(projectId));
      if (onSuccess) {
        onSuccess();
      }
    }
  };
